import React, { useState, useEffect } from 'react';
import { WifiOff } from 'lucide-react';
import { offlineQueue } from '../utils/offline-queue';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from './ui/tooltip';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from './ui/dialog';
import { useLocale } from '../contexts/LocaleContext';
import { cn } from '../lib/utils';

interface OfflineIndicatorProps {
  variant?: 'sidebar' | 'floating';
  isCollapsed?: boolean;
}

export const OfflineIndicator: React.FC<OfflineIndicatorProps> = ({ variant = 'sidebar', isCollapsed = false }) => {
  const [isOnline, setIsOnline] = useState(navigator.onLine);
  const [queuedOperations, setQueuedOperations] = useState(0);
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const { t } = useLocale();

  useEffect(() => {
    let wasOffline = !navigator.onLine;

    const checkOnlineStatus = () => {
      const isCurrentlyOnline = navigator.onLine;
      const previouslyOffline = wasOffline;
      
      if (isCurrentlyOnline) {
        wasOffline = false;
        setIsOnline(true);
        
        if (previouslyOffline && offlineQueue.getQueueLength() > 0) {
          setTimeout(() => offlineQueue.processQueue(), 100);
        }
      } else {
        wasOffline = true;
        setIsOnline(false);
      }
    };

    const handleOnline = () => {
      wasOffline = false;
      setIsOnline(true);
      if (offlineQueue.getQueueLength() > 0) {
        setTimeout(() => offlineQueue.processQueue(), 100);
      }
    };
    
    const handleOffline = () => {
      wasOffline = true;
      setIsOnline(false);
    };

    checkOnlineStatus();

    window.addEventListener('online', handleOnline);
    window.addEventListener('offline', handleOffline);

    const interval = setInterval(checkOnlineStatus, 2000);

    return () => {
      window.removeEventListener('online', handleOnline);
      window.removeEventListener('offline', handleOffline);
      clearInterval(interval);
    };
  }, []);

  useEffect(() => {
    const updateQueueStatus = () => {
      setQueuedOperations(offlineQueue.getQueueLength());
    };

    const handleQueueUpdate = () => {
      updateQueueStatus();
    };

    const handleQueueProcessingFinished = () => {
      updateQueueStatus();
    };

    updateQueueStatus();

    const interval = setInterval(() => {
      updateQueueStatus();
      if (navigator.onLine && offlineQueue.getQueueLength() > 0) {
        void offlineQueue.processQueue();
      }
    }, 1000);
    window.addEventListener('offline-queue-updated', handleQueueUpdate);
    window.addEventListener('offline-queue-processing-finished', handleQueueProcessingFinished);

    return () => {
      clearInterval(interval);
      window.removeEventListener('offline-queue-updated', handleQueueUpdate);
      window.removeEventListener('offline-queue-processing-finished', handleQueueProcessingFinished);
    };
  }, []);

  if (isOnline && queuedOperations === 0) {
    return null;
  }

  const getStatusText = (): string => {
    if (!isOnline && queuedOperations > 0) {
      const isPlural = queuedOperations !== 1;
      const plural = isPlural ? 's' : '';
      let template = t('offline_with_queued_operations') || `Offline. ${queuedOperations} operation${plural} queued`;
      template = template.replace(/\{count\}/g, String(queuedOperations));
      template = template.replace(/\{plural\}/g, plural);
      return template;
    }
    if (!isOnline) {
      return t('offline') || 'Offline. Changes are saved locally and will sync when online.';
    }
    if (queuedOperations > 0) {
      const isPlural = queuedOperations !== 1;
      const plural = isPlural ? 's' : '';
      let template = t('syncing_queued_operations') || `Syncing ${queuedOperations} operation${plural}...`;
      template = template.replace(/\{count\}/g, String(queuedOperations));
      template = template.replace(/\{plural\}/g, plural);
      return template;
    }
    return '';
  };

  const getDetailedStatus = (): { title: string; description: string } => {
    if (!isOnline && queuedOperations > 0) {
      const isPlural = queuedOperations !== 1;
      const plural = isPlural ? 's' : '';
      const verb = isPlural ? 'are' : 'is';
      const verbEs = isPlural ? 'están' : 'está';
      const syncVerbEs = isPlural ? 'se sincronizarán' : 'se sincronizará';
      let template = t('offline_with_queued_operations_detail') || `You're currently offline. ${queuedOperations} operation${plural} ${verb} queued and will be synced when you're back online. Your changes are saved locally.`;
      template = template.replace(/\{count\}/g, String(queuedOperations));
      template = template.replace(/\{plural\}/g, plural);
      template = template.replace(/\{verb\}/g, verb);
      template = template.replace(/\{verb_es\}/g, verbEs);
      template = template.replace(/\{sync_verb_es\}/g, syncVerbEs);
      return {
        title: t('offline') || 'Offline',
        description: template,
      };
    }
    if (!isOnline) {
      return {
        title: t('offline') || 'Offline',
        description: t('offline_detail') || 'You\'re currently offline. Your changes are saved locally and will sync automatically when you\'re back online.',
      };
    }
    if (queuedOperations > 0) {
      const isPlural = queuedOperations !== 1;
      const plural = isPlural ? 's' : '';
      let template = t('syncing_queued_operations_detail') || `Syncing ${queuedOperations} operation${plural} to the server. This may take a moment.`;
      template = template.replace(/\{count\}/g, String(queuedOperations));
      template = template.replace(/\{plural\}/g, plural);
      return {
        title: t('syncing') || 'Syncing',
        description: template,
      };
    }
    return {
      title: '',
      description: '',
    };
  };

  const status = getDetailedStatus();

  if (variant === 'floating') {
    return (
      <>
        <TooltipProvider>
          <Tooltip>
            <TooltipTrigger asChild>
              <button
                onClick={() => setIsDialogOpen(true)}
                style={{
                  position: 'fixed',
                  bottom: '1rem',
                  right: '1rem',
                  zIndex: 50,
                  width: '40px',
                  height: '40px',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  cursor: 'pointer',
                  backgroundColor: 'rgba(255, 255, 255, 0.9)',
                  backdropFilter: 'blur(8px)',
                  border: '1px solid rgba(239, 68, 68, 0.2)',
                  borderRadius: '50%',
                  padding: 0,
                  boxShadow: '0 2px 8px rgba(0, 0, 0, 0.15)',
                  transition: 'all 0.2s',
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.backgroundColor = 'rgba(255, 255, 255, 1)';
                  e.currentTarget.style.borderColor = 'rgba(239, 68, 68, 0.4)';
                  e.currentTarget.style.transform = 'scale(1.05)';
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.backgroundColor = 'rgba(255, 255, 255, 0.9)';
                  e.currentTarget.style.borderColor = 'rgba(239, 68, 68, 0.2)';
                  e.currentTarget.style.transform = 'scale(1)';
                }}
                role="button"
                tabIndex={0}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' || e.key === ' ') {
                    e.preventDefault();
                    setIsDialogOpen(true);
                  }
                }}
                aria-label={t('connection_status') || 'Connection status'}
              >
                <WifiOff className="h-5 w-5" style={{ color: '#ef4444' }} />
              </button>
            </TooltipTrigger>
            <TooltipContent side="left" className="max-w-xs">
              <p className="text-sm">{getStatusText()}</p>
            </TooltipContent>
          </Tooltip>
        </TooltipProvider>

        <Dialog open={isDialogOpen} onOpenChange={setIsDialogOpen}>
          <DialogContent>
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2">
                <WifiOff className="h-5 w-5" style={{ color: '#ef4444' }} />
                {status.title}
              </DialogTitle>
              <DialogDescription className="pt-2">
                {status.description}
              </DialogDescription>
            </DialogHeader>
          </DialogContent>
        </Dialog>
      </>
    );
  }

  return (
    <>
      <TooltipProvider>
        <Tooltip>
          <TooltipTrigger asChild>
            <div
              className={cn(
                "flex h-[38px] items-center gap-3 rounded-md px-3 text-sm font-medium transition-all hover:bg-accent hover:text-accent-foreground",
                isCollapsed ? "justify-center text-center" : "",
                "cursor-pointer"
              )}
              style={{ color: '#ef4444' }}
              onClick={() => setIsDialogOpen(true)}
              role="button"
              tabIndex={0}
              onKeyDown={(e) => {
                if (e.key === 'Enter' || e.key === ' ') {
                  e.preventDefault();
                  setIsDialogOpen(true);
                }
              }}
              aria-label={t('connection_status') || 'Connection status'}
            >
              <WifiOff className="h-4 w-4" />
              {!isCollapsed && (
                <span className="flex-1 text-left">
                  {isOnline ? t('online') : t('offline')}
                  {queuedOperations > 0 && ` (${queuedOperations})`}
                </span>
              )}
            </div>
          </TooltipTrigger>
          <TooltipContent side="right" className="max-w-xs">
            <p className="text-sm">{getStatusText()}</p>
          </TooltipContent>
        </Tooltip>
      </TooltipProvider>

      <Dialog open={isDialogOpen} onOpenChange={setIsDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <WifiOff className="h-5 w-5" style={{ color: '#ef4444' }} />
              {status.title}
            </DialogTitle>
            <DialogDescription className="pt-2">
              {status.description}
            </DialogDescription>
          </DialogHeader>
        </DialogContent>
      </Dialog>
    </>
  );
};

